/**
 * 🟥 CUADRO NEGRO V4.1 — ConflictResolutionModal
 *
 * Aparece cuando POST /api/github/pull-with-conflicts devuelve status="conflicts".
 * Muestra cada archivo en conflicto con tres acciones:
 *   - "Resolver con IA": llama /api/github/resolve-conflicts.
 *   - "Mantener local": no aplica el remoto (conserva contenido actual).
 *   - "Mantener GitHub": sobrescribe local con remoto (aplicar via /workspace/projects/{id}/files).
 */
import { useState } from 'react';
import axios from 'axios';
import { GitMerge, X, Wand2, Github, FileCode, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export const ConflictResolutionModal = ({
  moduleId,
  conflicts,
  onClose,
  onResolved,
}) => {
  const { language } = useLanguage();
  const isEs = (language || 'es').toLowerCase().startsWith('es');
  const [pending, setPending] = useState(new Set(conflicts.map((c) => c.path)));
  const [busy, setBusy] = useState(null); // path actualmente procesando
  const [globalBusy, setGlobalBusy] = useState(false);

  if (!conflicts?.length) return null;

  const markDone = (path) => {
    setPending((prev) => {
      const next = new Set(prev);
      next.delete(path);
      return next;
    });
  };

  const resolveOne = async (path, action) => {
    if (busy || globalBusy) return;
    setBusy(path);
    const conflict = conflicts.find((c) => c.path === path);
    try {
      if (action === 'ai') {
        const res = await axios.post(`${API}/github/resolve-conflicts`, {
          module_id: moduleId,
          conflicts: [conflict],
        });
        const r = res.data?.resolved?.[0];
        if (r?.status === 'merged') {
          toast.success(isEs ? `IA resolvió ${path}` : `AI resolved ${path}`);
          markDone(path);
        } else {
          toast.error(isEs ? `Falló IA en ${path}: ${r?.error || 'unknown'}` : `AI failed on ${path}: ${r?.error || 'unknown'}`);
        }
      } else if (action === 'local') {
        // No-op en backend: dejar contenido local. Solo marcar done.
        toast.info(isEs ? `Manteniendo local en ${path}` : `Keeping local ${path}`);
        markDone(path);
      } else if (action === 'remote') {
        // Sobrescribir local con remote via PUT /workspace/projects/{id}/files.
        await axios.put(`${API}/workspace/projects/${moduleId}/files`, {
          files: [{ path, content: conflict.remote }],
        });
        toast.success(isEs ? `Sobrescrito con GitHub: ${path}` : `Overwritten with GitHub: ${path}`);
        markDone(path);
      }
    } catch (e) {
      toast.error(isEs ? `Error en ${path}` : `Error on ${path}`);
    } finally {
      setBusy(null);
    }
  };

  const finish = () => {
    window.dispatchEvent(new CustomEvent('damyxia-reload-internal-module', { detail: { moduleId } }));
    onResolved?.();
    onClose?.();
  };

  const resolveAllWithAI = async () => {
    setGlobalBusy(true);
    try {
      const remaining = conflicts.filter((c) => pending.has(c.path));
      if (remaining.length === 0) return;
      const res = await axios.post(`${API}/github/resolve-conflicts`, {
        module_id: moduleId,
        conflicts: remaining,
      });
      const resolved = res.data?.resolved || [];
      let ok = 0;
      for (const r of resolved) {
        if (r.status === 'merged') {
          markDone(r.path);
          ok += 1;
        }
      }
      const failed = resolved.length - ok;
      if (ok > 0) toast.success(isEs ? `IA resolvió ${ok} archivo(s)` : `AI resolved ${ok} file(s)`);
      if (failed > 0) toast.error(isEs ? `${failed} archivo(s) fallaron` : `${failed} file(s) failed`);
    } catch {
      toast.error(isEs ? 'Resolución con IA falló' : 'AI resolution failed');
    } finally {
      setGlobalBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="conflict-modal">
      <div className="w-full max-w-3xl max-h-[85vh] bg-slate-950 border border-amber-500/40 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-amber-950/30">
          <div className="flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-amber-400" />
            <h3 className="text-sm font-semibold text-amber-300">
              {isEs ? `Se detectaron ${conflicts.length} conflictos GitHub ↔ Workspace` : `${conflicts.length} GitHub ↔ Workspace conflicts detected`}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resolveAllWithAI}
              disabled={globalBusy || pending.size === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-indigo-500 hover:bg-indigo-400 text-white disabled:opacity-50"
              data-testid="conflict-resolve-all-ai-btn"
            >
              {globalBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              <span>{isEs ? 'Resolver todo con IA' : 'Resolve all with AI'}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-slate-400 hover:text-white rounded"
              data-testid="conflict-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {conflicts.map((c) => {
            const resolved = !pending.has(c.path);
            const isThisBusy = busy === c.path;
            return (
              <div
                key={c.path}
                className={`rounded-lg border p-3 ${resolved ? 'border-emerald-500/40 bg-emerald-950/20' : 'border-slate-800 bg-slate-900/60'}`}
                data-testid={`conflict-row-${c.path}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 text-sm text-slate-200 font-mono truncate">
                    <FileCode className="w-4 h-4 shrink-0 text-amber-400" />
                    <span className="truncate">{c.path}</span>
                  </div>
                  {resolved ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                      <Check className="w-3.5 h-3.5" />
                      {isEs ? 'Resuelto' : 'Resolved'}
                    </span>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => resolveOne(c.path, 'ai')}
                        disabled={isThisBusy || globalBusy}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 disabled:opacity-50"
                      >
                        {isThisBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                        {isEs ? 'IA' : 'AI'}
                      </button>
                      <button
                        type="button"
                        onClick={() => resolveOne(c.path, 'local')}
                        disabled={isThisBusy || globalBusy}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-slate-700/60 text-slate-200 hover:bg-slate-700 disabled:opacity-50"
                      >
                        {isEs ? 'Local' : 'Local'}
                      </button>
                      <button
                        type="button"
                        onClick={() => resolveOne(c.path, 'remote')}
                        disabled={isThisBusy || globalBusy}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 disabled:opacity-50"
                      >
                        <Github className="w-3 h-3" />
                        {isEs ? 'GitHub' : 'GitHub'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-slate-500 mb-1">{isEs ? 'Local' : 'Local'}</div>
                    <pre className="bg-black/40 rounded p-2 max-h-32 overflow-auto text-slate-300 whitespace-pre-wrap">{(c.local || '').slice(0, 600)}{(c.local || '').length > 600 ? '…' : ''}</pre>
                  </div>
                  <div>
                    <div className="text-slate-500 mb-1">{isEs ? 'GitHub' : 'GitHub'}</div>
                    <pre className="bg-black/40 rounded p-2 max-h-32 overflow-auto text-slate-300 whitespace-pre-wrap">{(c.remote || '').slice(0, 600)}{(c.remote || '').length > 600 ? '…' : ''}</pre>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800 bg-slate-950">
          <div className="text-xs text-slate-400">
            {pending.size > 0
              ? (isEs ? `${pending.size} conflicto(s) pendientes` : `${pending.size} conflict(s) remaining`)
              : (isEs ? 'Todos resueltos' : 'All resolved')}
          </div>
          <button
            type="button"
            onClick={finish}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-emerald-500 hover:bg-emerald-400 text-white font-medium"
            data-testid="conflict-finish-btn"
          >
            {isEs ? 'Cerrar y refrescar' : 'Close & refresh'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;
